import api, { ApiResponse, QueryPayload, QueryResponse } from './api';

export interface Tag {
  id: string;
  workspace_id: string;
  name: string;
  color: string;
  created_at: string;
  updated_at: string;
}

export const tagsService = {
  query: async (filters: { limit?: number; offset?: number } = {}): Promise<{ data: Tag[]; total: number }> => {
    const payload: QueryPayload = {
      offset: filters.offset ?? 0,
      limit: filters.limit ?? 500,
      order_by: 'tag.name',
      order: 'asc',
    };
    const response = await api.post<QueryResponse<Tag>>('/tags/query', payload);
    return { data: response.data.data, total: response.data._meta?.total ?? 0 };
  },

  getById: async (id: string): Promise<Tag> => {
    const response = await api.get<ApiResponse<Tag>>(`/tags/${id}`);
    return response.data.data!;
  },

  // Idempotent server-side: returns the existing tag if the name already exists.
  create: async (data: { name: string; color?: string }): Promise<Tag> => {
    const response = await api.post<ApiResponse<Tag>>('/tags', data);
    return response.data.data!;
  },

  update: async (id: string, data: Partial<Pick<Tag, 'name' | 'color'>>): Promise<Tag> => {
    const response = await api.patch<ApiResponse<Tag>>(`/tags/${id}`, data);
    return response.data.data!;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/tags/${id}`);
  },
};

// A neutral, accessible default palette for new tags (Material design hues).
export const TAG_COLORS = [
  '#e53935', '#d81b60', '#8e24aa', '#5e35b1', '#3949ab',
  '#1e88e5', '#00897b', '#43a047', '#f4511e', '#6d4c41',
  '#607d8b', '#fb8c00',
];

/** Pick a readable text color (black/white) for a given hex background. */
export function contrastText(hex: string): string {
  const h = hex.replace('#', '');
  if (h.length !== 6) return '#fff';
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  // Perceived luminance (YIQ)
  return (r * 299 + g * 587 + b * 114) / 1000 >= 140 ? '#000' : '#fff';
}
